import { useCallback } from 'react';
import * as ImagePicker from 'expo-image-picker';
import * as ImageManipulator from 'expo-image-manipulator';
import { Platform } from 'react-native';

export interface ImagePickerResult {
  formData: FormData;
  uri: string;
}

interface UseImagePickerReturn {
  pickAndPrepareImage: () => Promise<ImagePickerResult | null>;
}

export function useImagePicker(): UseImagePickerReturn {
  const createCircularCrop = useCallback(async (uri: string): Promise<string> => {
    // First, get image dimensions
    const imageInfo = await ImageManipulator.manipulateAsync(
      uri,
      [],
      { base64: false }
    );

    const { width, height } = imageInfo;
    const size = Math.min(width, height);
    const originX = (width - size) / 2;
    const originY = (height - size) / 2;

    // Crop to square from center
    const cropped = await ImageManipulator.manipulateAsync(
      uri,
      [
        {
          crop: {
            originX,
            originY,
            width: size,
            height: size,
          },
        },
      ],
      { base64: false }
    );

    return cropped.uri;
  }, []);

  const pickAndPrepareImage = useCallback(async (): Promise<ImagePickerResult | null> => {
    try {
      // Pick image — no media library permission needed on Android 13+ (uses system photo picker)
      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ['images'],
        allowsEditing: false,
        quality: 0.9,
        allowsMultipleSelection: false,
      });

      if (result.canceled || !result.assets || result.assets.length === 0) {
        return null;
      }

      const selectedAsset = result.assets[0];
      const originalUri = selectedAsset.uri;

      // Create circular crop (square crop from center)
      const croppedUri = await createCircularCrop(originalUri);

      // Resize to reasonable size for profile picture (max 512x512)
      const resized = await ImageManipulator.manipulateAsync(
        croppedUri,
        [{ resize: { width: 512, height: 512 } }],
        {
          compress: 0.85,
          format: ImageManipulator.SaveFormat.JPEG,
          base64: false,
        }
      );

      // Read the file as base64 for upload
      const response = await fetch(resized.uri);
      const blob = await response.blob();

      // Create FormData
      const formData = new FormData();
      const filename = resized.uri.split('/').pop() || 'photo.jpg';

      if (Platform.OS === 'web') {
        // Web: blob can be appended directly
        formData.append('file', blob, filename);
      } else {
        // Native: we need to use a different approach
        // Create a file-like object
        const file = {
          uri: resized.uri,
          type: 'image/jpeg',
          name: filename,
        } as any;
        formData.append('file', file);
      }

      return { formData, uri: resized.uri };
    } catch (error) {
      console.error('Failed to prepare image:', error);
      throw error;
    }
  }, [createCircularCrop]);

  return {
    pickAndPrepareImage,
  };
}